// CodeMirror-backed console prompt with lua autocomplete.
//
// - Enter           -> push "execute_snippet" with the editor value. The
//                      autocomplete extension's own Enter handler runs at a
//                      higher precedence when its tooltip is open, so this
//                      only fires when the user is *not* picking a completion.
//                      Shift/Meta/Ctrl+Enter falls through to the default
//                      newline keymap.
// - ArrowUp/Down    -> push "history_prev" / "history_next" when the caret
//                      is on the first/last line so multi-line editing is
//                      unaffected. The server owns the history and cursor.
// - input           -> debounced "snippet_changed" so the LiveView's
//                      :current_snippet assign stays in sync.
// - handleEvent("console:set-value") -> overwrite the editor value
//                      (used by execute_snippet and history walks; needed
//                      because phx-update="ignore" keeps morphdom away).

import luaCompletions from "../code/lua_completions.json";

const DEBOUNCE_MS = 150;

const createLuaCompletions = (context) => {
  const word = context.matchBefore(/[\w.]*/);
  if (word.from == word.to && !context.explicit) return null;
  return {
    from: word.from,
    options: luaCompletions,
  };
};

export const ConsolePrompt = {
  async mounted() {
    this.view = null;
    this.changeTimeout = null;
    this.pendingChange = null;

    const [
      { EditorView, keymap, placeholder, drawSelection, highlightSpecialChars },
      { StreamLanguage, syntaxHighlighting, defaultHighlightStyle },
      { lua },
      { draculaInit },
      { autocompletion },
      { defaultKeymap, history, historyKeymap },
    ] = await Promise.all([
      import("@codemirror/view"),
      import("@codemirror/language"),
      import("@codemirror/legacy-modes/mode/lua"),
      import("@uiw/codemirror-theme-dracula"),
      import("@codemirror/autocomplete"),
      import("@codemirror/commands"),
    ]);

    const initialContent = this.el.dataset.initialContent ?? "";
    const placeholderText =
      this.el.dataset.placeholder ||
      "Type a script and press Enter to run (Shift+Enter for newline)";

    this.view = new EditorView({
      doc: initialContent,
      parent: this.el,
      extensions: [
        keymap.of([...this.promptKeymap(), ...defaultKeymap, ...historyKeymap]),
        history(),
        highlightSpecialChars(),
        drawSelection(),
        StreamLanguage.define(lua),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        autocompletion({ override: [createLuaCompletions] }),
        placeholder(placeholderText),
        draculaInit({
          settings: {
            background: "transparent",
            gutterBackground: "transparent",
            lineHighlight: "transparent",
          },
        }),
        EditorView.lineWrapping,
        EditorView.theme({
          "&": {
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
          },
          "&.cm-focused": { outline: "none" },
          ".cm-scroller": {
            fontFamily: "inherit",
            maxHeight: "8rem",
            overflow: "auto",
          },
          ".cm-content": { padding: "0.25rem 0" },
          ".cm-gutters": { display: "none" },
        }),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.scheduleSnippetChanged(this.getContent());
          }
        }),
      ],
    });

    this.handleEvent("console:set-value", ({ value }) => {
      this.setContent(value ?? "");
      this.placeCaretAtEnd();
    });
  },

  destroyed() {
    this.flushSnippetChanged();
    if (this.view) {
      this.view.destroy();
      this.view = null;
    }
  },

  promptKeymap() {
    return [
      {
        key: "Enter",
        run: () => {
          this.submit();
          return true;
        },
      },
      {
        key: "ArrowUp",
        run: (view) => {
          const pos = view.state.selection.main.head;
          if (view.state.doc.lineAt(pos).number !== 1) return false;
          this.flushSnippetChanged();
          this.pushEvent("history_prev", {});
          return true;
        },
      },
      {
        key: "ArrowDown",
        run: (view) => {
          const pos = view.state.selection.main.head;
          if (view.state.doc.lineAt(pos).number !== view.state.doc.lines) return false;
          this.flushSnippetChanged();
          this.pushEvent("history_next", {});
          return true;
        },
      },
    ];
  },

  submit() {
    if (this.el.dataset.running === "true") return;
    const value = this.getContent();
    if (!value.trim()) return;
    this.flushSnippetChanged();
    this.pushEvent("execute_snippet", { snippet: value });
  },

  scheduleSnippetChanged(content) {
    if (this.changeTimeout) clearTimeout(this.changeTimeout);
    this.pendingChange = content;
    this.changeTimeout = setTimeout(() => {
      this.pushEvent("snippet_changed", { content: this.pendingChange });
      this.changeTimeout = null;
      this.pendingChange = null;
    }, DEBOUNCE_MS);
  },

  flushSnippetChanged() {
    if (this.changeTimeout) {
      clearTimeout(this.changeTimeout);
      this.changeTimeout = null;
      if (this.pendingChange != null) {
        this.pushEvent("snippet_changed", { content: this.pendingChange });
        this.pendingChange = null;
      }
    }
  },

  getContent() {
    return this.view?.state.doc.toString() ?? "";
  },

  setContent(content) {
    if (!this.view) return;
    this.view.dispatch({
      changes: { from: 0, to: this.view.state.doc.length, insert: content },
    });
  },

  placeCaretAtEnd() {
    if (!this.view) return;
    const len = this.view.state.doc.length;
    this.view.dispatch({ selection: { anchor: len, head: len } });
  },
};
